import { useState, useCallback } from 'react';
import { useClearStaleEmrAttemptWhenPushFailedReleased } from './useClearStaleEmrAttemptWhenPushFailedReleased';
import { scheduleRogerExistingPatient } from '../../services/api';
import { getSurfaceableError } from '../../utils/apiErrorDetail';
import type { RogerOnboardResultWithSurfaceable } from './useRogerCreation';

interface UseRogerSchedulingOptions {
  intakeId: string | undefined;
  /** Global intake status; used to clear stale client errors after push_failed is reset. */
  intakeStatus?: string | null;
}

interface UseRogerSchedulingReturn {
  schedulingInRoger: boolean;
  rogerSchedulingResult: RogerOnboardResultWithSurfaceable | null;
  handleScheduleInRoger: () => Promise<void>;
}

/**
 * Hook for scheduling a SOC or ROC visit on an existing Roger patient.
 *
 * Unlike `useRogerCreation`, this does NOT create a new patient. It calls the
 * `schedule_roger_existing_patient` endpoint, which creates the Admission / Episode / Visit
 * records on the intake's matched patient based on `kinnser_visit_type` and
 * `kinnser_creation_type` stored in intake.fields (these keys are shared by
 * both Kinnser and Roger scheduling flows; the `kinnser_` prefix is historical).
 */
export function useRogerScheduling({
  intakeId,
  intakeStatus,
}: UseRogerSchedulingOptions): UseRogerSchedulingReturn {
  const [schedulingInRoger, setSchedulingInRoger] = useState(false);
  const [rogerSchedulingResult, setRogerSchedulingResult] =
    useState<RogerOnboardResultWithSurfaceable | null>(null);

  const clearRogerSchedulingResult = useCallback(() => {
    setRogerSchedulingResult(null);
  }, []);
  useClearStaleEmrAttemptWhenPushFailedReleased(
    intakeId,
    intakeStatus,
    clearRogerSchedulingResult
  );

  const handleScheduleInRoger = useCallback(async () => {
    if (!intakeId) return;

    setSchedulingInRoger(true);
    setRogerSchedulingResult(null);

    try {
      const response = await scheduleRogerExistingPatient({
        path: { intake_id: intakeId },
        throwOnError: true,
      });

      if (response.data) {
        setRogerSchedulingResult(response.data);
      }
    } catch (error) {
      console.error('Error scheduling visit on existing Roger patient:', error);
      // Only show a server message when it is flagged surfaceable; otherwise use
      // generic copy (do not echo raw `detail` — see CLAUDE.md error-handling rule).
      const surfaceable = getSurfaceableError(error);
      setRogerSchedulingResult(
        surfaceable
          ? {
              success: false,
              message: surfaceable.message,
              surfaceable: true,
              error_code: surfaceable.errorCode,
            }
          : {
              success: false,
              message: 'An unexpected error occurred. Please try again.',
            }
      );
    } finally {
      setSchedulingInRoger(false);
    }
  }, [intakeId]);

  return {
    schedulingInRoger,
    rogerSchedulingResult,
    handleScheduleInRoger,
  };
}
